"use client";

import * as React from "react";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useBranch } from "@/context/branch-context";
import { columns } from "./columns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";

type BranchesProps = {
  getLink?: (branchId: string) => string;
};

const BRANCH_FILTER_STORAGE_KEY = "pizza-admin:branch-list-filter:v1";

/** Names shown in the column picker for the columns a user can hide. */
const COLUMN_LABEL_KEYS: Record<string, TranslationKey> = {
  name: "branches.columns.name",
  bb_branch_id: "branches.columns.bbBranchId",
  enable_acquisition: "branches.columns.acquisition",
  enable_bb_trigger: "branches.columns.bbTrigger",
  enable_evaluation: "branches.columns.evaluation",
};

export default function Branches({ getLink }: BranchesProps) {
  const { t, locale } = useLocalization();
  const { branches, selectBranch, selectedBranchId, refetchBranches } = useBranch();
  
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({
    name: true,
    bb_branch_id: true,
    enable_acquisition: true,
    enable_bb_trigger: true,
    enable_evaluation: true,
    actions: true,
  });
  const [rowSelection, setRowSelection] = React.useState({});
  const [nameFilter, setNameFilter] = useLocalStorageState(
    BRANCH_FILTER_STORAGE_KEY,
    "",
  );
  
  // Store pagination state to preserve it during refetch
  const [paginationState, setPaginationState] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Filter branches based on name
  const filteredBranches = React.useMemo(() => {
    if (!branches) return [];
    if (!nameFilter.trim()) return branches;
    
    return branches.filter(branch =>
      branch.name.toLowerCase().includes(nameFilter.toLowerCase()) ||
      branch.bb_branch_id.toLowerCase().includes(nameFilter.toLowerCase())
    );
  }, [branches, nameFilter]);

  const handleUpdate = React.useCallback(() => {
    refetchBranches();
  }, [refetchBranches]);

  // Memoize columns to prevent unnecessary re-renders (the locale keeps their texts current)
  const memoizedColumns = React.useMemo(
    () => columns(getLink, selectBranch, handleUpdate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getLink, selectBranch, handleUpdate, locale]
  );

  const table = useReactTable({
    data: filteredBranches,
    columns: memoizedColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPaginationState,
    onRowSelectionChange: (updater) => {
      const newSelection = typeof updater === "function" ? updater(rowSelection) : updater;
      setRowSelection(newSelection);
      
      // Auto-select branch when row is selected (now using branch ID directly)
      const selectedBranchId = Object.keys(newSelection)[0];
      if (selectedBranchId) {
        selectBranch(selectedBranchId);
      }
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: paginationState,
    },
    initialState: {
      pagination: paginationState,
    },
    // Prevent automatic resets when data changes
    autoResetPageIndex: false,
    autoResetExpanded: false,
    // Use branch ID for row selection to maintain selection across data updates
    getRowId: (row) => row.id,
  });

  return (
    <div className="w-full gap-4 flex flex-col">
      <div className="flex items-center gap-2">
        <Input
          placeholder={t("branches.list.filterPlaceholder")}
          value={nameFilter}
          onChange={(event) => setNameFilter(event.target.value)}
          className="max-w-sm"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              {t("common.columns")} <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {COLUMN_LABEL_KEYS[column.id]
                      ? t(COLUMN_LABEL_KEYS[column.id])
                      : column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={
                    row.id === selectedBranchId
                      ? "bg-muted/50"
                      : ""
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  {t("common.noResults")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">{t("common.perPage")}</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex min-w-[100px] items-center justify-center whitespace-nowrap px-2 text-sm font-medium">
            {t("common.page", {
              page: table.getState().pagination.pageIndex + 1,
              pages: table.getPageCount(),
            })}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">{t("common.firstPage")}</span>
              <ChevronsLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">{t("common.previousPage")}</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">{t("common.nextPage")}</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">{t("common.lastPage")}</span>
              <ChevronsRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
